import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Pencil, Trash2, Loader2, AlertTriangle, Star, Search, BookOpen, BarChart3, TrendingUp, MessageCircleQuestion, Copy, CheckCircle2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { MultiSelect } from "../../components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "../../components/ui/alert-dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toast } from "sonner";

interface TargetValue {
  id: number;
  name: string;
  description: string | null;
  category: string | null;
  createdBy: { id: number; name: string } | null;
}

const ValuesManagement: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_SETTINGS)) {
    return <AccessRestricted />;
  }

  const [targetValues, setTargetValues] = useState<TargetValue[]>([]);
  const [targetValuesLoading, setTargetValuesLoading] = useState(false);
  const [targetValueSubmitting, setTargetValueSubmitting] = useState(false);
  const [showValueDialog, setShowValueDialog] = useState(false);
  const [editingValue, setEditingValue] = useState<TargetValue | null>(null);
  const [valueName, setValueName] = useState("");
  const [valueDescription, setValueDescription] = useState("");
  const [valueCategory, setValueCategory] = useState("");
  const [valueToDelete, setValueToDelete] = useState<TargetValue | null>(null);
  const [valueDeleteLoading, setValueDeleteLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("core-values");
  const [questionCount, setQuestionCount] = useState(0);

  // Interview Questions state
  interface InterviewQuestion {
    id: number;
    text: string;
    category: string | null;
    level: string | null;
    targetValues: string[];
    description: string | null;
    followUpQuestions: string[];
    tags: string[];
  }
  const [interviewQuestions, setInterviewQuestions] = useState<InterviewQuestion[]>([]);
  const [interviewQuestionsLoading, setInterviewQuestionsLoading] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<InterviewQuestion | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [questionCategory, setQuestionCategory] = useState("");
  const [questionLevel, setQuestionLevel] = useState("");
  const [questionTargetValues, setQuestionTargetValues] = useState<string[]>([]);
  const [questionDescription, setQuestionDescription] = useState("");
  const [questionFollowUps, setQuestionFollowUps] = useState<string[]>([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [questionTags, setQuestionTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{ id: number; text: string } | null>(null);
  const [questionDeleteLoading, setQuestionDeleteLoading] = useState(false);
  const [questionSearchQuery, setQuestionSearchQuery] = useState("");

  useEffect(() => {
    fetchTargetValues();
    fetchQuestionCount();
  }, []);

  useEffect(() => {
    if (activeTab === "question-library") {
      fetchInterviewQuestions();
    }
  }, [activeTab]);

  const fetchTargetValues = async () => {
    setTargetValuesLoading(true);
    try {
      const res = await api.get<{ success: boolean; values: TargetValue[] }>("/api/target-values");
      if (res.success) setTargetValues(res.values);
    } catch (err) {
      console.error("Failed to load target values:", err);
    } finally {
      setTargetValuesLoading(false);
    }
  };

  const fetchQuestionCount = async () => {
    try {
      const res = await api.get<{ success: boolean; questions: any[] }>("/api/interview-questions");
      if (res.success) setQuestionCount(res.questions.length);
    } catch (err) {
      console.error("Failed to load question count:", err);
    }
  };

  const filteredValues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return targetValues;
    return targetValues.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.description && v.description.toLowerCase().includes(q)) ||
        (v.category && v.category.toLowerCase().includes(q))
    );
  }, [searchQuery, targetValues]);

  const openAddValueDialog = () => {
    setEditingValue(null);
    setValueName("");
    setValueDescription("");
    setValueCategory("");
    setShowValueDialog(true);
  };

  const openEditValueDialog = (value: TargetValue) => {
    setEditingValue(value);
    setValueName(value.name);
    setValueDescription(value.description || "");
    setValueCategory(value.category || "");
    setShowValueDialog(true);
  };

  const handleSaveValue = async () => {
    if (!valueName.trim()) {
      toast.error("Value name is required");
      return;
    }
    setTargetValueSubmitting(true);
    try {
      if (editingValue) {
        const res = await api.put<{ success: boolean }>(`/api/target-values/${editingValue.id}`, {
          name: valueName.trim(),
          description: valueDescription.trim() || null,
          category: valueCategory.trim() || null,
        });
        if (res.success) {
          toast.success("Target value updated");
          setTargetValues((prev) =>
            prev.map((v) => (v.id === editingValue.id ? { ...v, name: valueName.trim(), description: valueDescription.trim() || null, category: valueCategory.trim() || null } : v))
          );
          setShowValueDialog(false);
        }
      } else {
        const res = await api.post<{ success: boolean; value: TargetValue }>("/api/target-values", {
          name: valueName.trim(),
          description: valueDescription.trim() || null,
          category: valueCategory.trim() || null,
        });
        if (res.success) {
          toast.success("Target value created");
          setTargetValues((prev) => [...prev, res.value]);
          setShowValueDialog(false);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save target value");
    } finally {
      setTargetValueSubmitting(false);
    }
  };

  const openDeleteValueDialog = (value: TargetValue) => {
    setValueToDelete(value);
    setDeleteModalOpen(true);
  };

  const handleDeleteValue = async () => {
    if (!valueToDelete) return;
    setValueDeleteLoading(true);
    try {
      await api.delete(`/api/target-values/${valueToDelete.id}`);
      setTargetValues((prev) => prev.filter((v) => v.id !== valueToDelete.id));
      toast.success("Target value deleted");
      setDeleteModalOpen(false);
      setValueToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete target value");
    } finally {
      setValueDeleteLoading(false);
    }
  };

  const fetchInterviewQuestions = async () => {
    setInterviewQuestionsLoading(true);
    try {
      const res = await api.get<{ success: boolean; questions: InterviewQuestion[] }>("/api/interview-questions");
      if (res.success) setInterviewQuestions(res.questions);
    } catch (err) {
      console.error("Failed to load interview questions:", err);
    } finally {
      setInterviewQuestionsLoading(false);
    }
  };

  const openAddQuestionDialog = () => {
    setEditingQuestion(null);
    setQuestionText("");
    setQuestionCategory("");
    setQuestionLevel("");
    setQuestionTargetValues([]);
    setQuestionDescription("");
    setQuestionFollowUps([]);
    setFollowUpInput("");
    setQuestionTags([]);
    setTagInput("");
    setShowQuestionDialog(true);
  };

  const openEditQuestionDialog = (question: InterviewQuestion) => {
    setEditingQuestion(question);
    setQuestionText(question.text);
    setQuestionCategory(question.category || "");
    setQuestionLevel(question.level || "");
    setQuestionTargetValues(question.targetValues || []);
    setQuestionDescription(question.description || "");
    setQuestionFollowUps(question.followUpQuestions || []);
    setFollowUpInput("");
    setQuestionTags(question.tags || []);
    setTagInput("");
    setShowQuestionDialog(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionText.trim()) {
      toast.error("Question text is required");
      return;
    }
    setQuestionSubmitting(true);
    try {
      const payload = {
        text: questionText.trim(),
        category: questionCategory.trim() || null,
        level: questionLevel.trim() || null,
        targetValues: questionTargetValues,
        description: questionDescription.trim() || null,
        followUpQuestions: questionFollowUps,
        tags: questionTags,
      };
      if (editingQuestion) {
        const res = await api.put<{ success: boolean }>(`/api/interview-questions/${editingQuestion.id}`, payload);
        if (res.success) {
          toast.success("Question updated");
          setInterviewQuestions((prev) =>
            prev.map((q) => (q.id === editingQuestion.id ? { ...q, ...payload } : q))
          );
          setShowQuestionDialog(false);
        }
      } else {
        const res = await api.post<{ success: boolean; question: InterviewQuestion }>("/api/interview-questions", payload);
        if (res.success) {
          toast.success("Question created");
          setInterviewQuestions((prev) => [...prev, res.question]);
          setQuestionCount((prev) => prev + 1);
          setShowQuestionDialog(false);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save question");
    } finally {
      setQuestionSubmitting(false);
    }
  };

  const openDeleteQuestionDialog = (question: { id: number; text: string }) => {
    setQuestionToDelete(question);
    setDeleteModalOpen(true);
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    setQuestionDeleteLoading(true);
    try {
      await api.delete(`/api/interview-questions/${questionToDelete.id}`);
      setInterviewQuestions((prev) => prev.filter((q) => q.id !== questionToDelete.id));
      setQuestionCount((prev) => Math.max(0, prev - 1));
      toast.success("Question deleted");
      setDeleteModalOpen(false);
      setQuestionToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete question");
    } finally {
      setQuestionDeleteLoading(false);
    }
  };

  const handleDuplicateQuestion = async (question: InterviewQuestion) => {
    try {
      const res = await api.post<{ success: boolean; question: InterviewQuestion }>("/api/interview-questions", {
        text: `${question.text} (Copy)`,
        category: question.category,
        level: question.level,
        targetValues: question.targetValues,
        description: question.description,
        followUpQuestions: question.followUpQuestions,
        tags: question.tags,
      });
      if (res.success) {
        toast.success("Question duplicated");
        setInterviewQuestions((prev) => [...prev, res.question]);
        setQuestionCount((prev) => prev + 1);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to duplicate question");
    }
  };

  const filteredQuestions = useMemo(() => {
    const q = questionSearchQuery.trim().toLowerCase();
    if (!q) return interviewQuestions;
    return interviewQuestions.filter(
      (iq) =>
        iq.text.toLowerCase().includes(q) ||
        (iq.description && iq.description.toLowerCase().includes(q)) ||
        (iq.category && iq.category.toLowerCase().includes(q)) ||
        (iq.level && iq.level.toLowerCase().includes(q)) ||
        (iq.tags && iq.tags.some((t) => t.toLowerCase().includes(q)))
    );
  }, [questionSearchQuery, interviewQuestions]);

  const statCards = [
    { label: "Core Values", value: targetValues.length, icon: <Star size={20} className="text-blue-500" />, iconBg: "bg-blue-50" },
    { label: "Questions", value: questionCount, icon: <BookOpen size={20} className="text-green-500" />, iconBg: "bg-green-50" },
    { label: "Avg Effectiveness", value: "—", icon: <BarChart3 size={20} className="text-purple-500" />, iconBg: "bg-purple-50" },
    { label: "Total Usage", value: "—", icon: <TrendingUp size={20} className="text-orange-500" />, iconBg: "bg-orange-50" },
  ];

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/recruitment")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Recruitment</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Star size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Values Management
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage core values and interview question library
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative"
          >
            <div className="flex justify-between items-start">
              <div className="z-10">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                  {stat.label}
                </p>
                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                  {stat.value}
                </h3>
              </div>
              <div className={`p-2.5 rounded-xl transition-colors z-10 ${stat.iconBg}`}>
                <div className="transition-colors">{stat.icon}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger
            value="core-values"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Core Values ({targetValues.length})
          </TabsTrigger>
          <TabsTrigger
            value="question-library"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Question Library ({questionCount})
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Core Values Tab */}
        <TabsContent value="core-values" className="mt-0 space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search values..."
                className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
              onClick={openAddValueDialog}
            >
              <Plus size={16} />
              <span>Add Value</span>
            </Button>
          </div>

          {/* Values Table */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {targetValuesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : filteredValues.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm font-medium text-gray-500">
                  {searchQuery.trim() ? "No values match your search." : "No target values yet. Add your first target value to get started."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                        Value
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Category
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Description
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Created By
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredValues.map((value) => (
                      <tr
                        key={value.id}
                        className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <p className="text-sm font-bold text-gray-900">{value.name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                            {value.category || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-600 font-medium max-w-md truncate">
                            {value.description || "—"}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-500 font-medium">
                            {value.createdBy?.name || "—"}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditValueDialog(value)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => openDeleteValueDialog(value)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Question Library Tab */}
        <TabsContent value="question-library" className="mt-0 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search questions..."
                className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
                value={questionSearchQuery}
                onChange={(e) => setQuestionSearchQuery(e.target.value)}
              />
            </div>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
              onClick={openAddQuestionDialog}
            >
              <Plus size={16} />
              <span>Add Question</span>
            </Button>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {interviewQuestionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircleQuestion size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">
                  {questionSearchQuery.trim() ? "No questions match your search." : "No questions yet. Add your first interview question to get started."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                        Question
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Category
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Level
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Values
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Usage
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Effectiveness
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuestions.map((q) => (
                      <tr
                        key={q.id}
                        className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="min-w-0 max-w-xs">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {q.text}
                            </p>
                            {q.tags && q.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {q.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100">
                            {q.category || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100">
                            {q.level || "All Levels"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {q.targetValues?.slice(0, 2).map((tv) => (
                              <span
                                key={tv}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200"
                              >
                                {tv}
                              </span>
                            ))}
                            {(q.targetValues?.length || 0) > 2 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold text-gray-500">
                                +{q.targetValues!.length - 2} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-center">
                            <p className="text-sm font-bold text-gray-900">—</p>
                            <p className="text-[10px] text-gray-400 font-medium">Avg: —</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-gray-900">—</span>
                            <CheckCircle2 size={14} className="text-green-500" />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditQuestionDialog(q)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDuplicateQuestion(q)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => openDeleteQuestionDialog(q)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1.5"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Values Distribution */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-1">Values Distribution</h3>
              <p className="text-sm text-gray-500 font-medium mb-5">How values are distributed across categories</p>
              <div className="space-y-3">
                {(() => {
                  const allCategories = ["Leadership", "Collaboration", "Excellence", "Integrity", "Innovation", "Other"];
                  const total = targetValues.length || 1;
                  const counts = allCategories.map((cat) => ({
                    name: cat,
                    count: targetValues.filter((v) => v.category === cat).length,
                  }));
                  const categoryStyles: Record<string, string> = {
                    Leadership: "bg-purple-100 text-purple-700 border-purple-200",
                    Collaboration: "bg-blue-100 text-blue-700 border-blue-200",
                    Excellence: "bg-green-100 text-green-700 border-green-200",
                    Integrity: "bg-amber-100 text-amber-700 border-amber-200",
                    Innovation: "bg-pink-100 text-pink-700 border-pink-200",
                    Other: "bg-gray-100 text-gray-700 border-gray-200",
                  };
                  return counts.map((c) => (
                    <div key={c.name} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold border shrink-0 ${categoryStyles[c.name]}`}>
                          {c.name}
                        </span>
                        <span className="text-sm text-gray-600 font-medium">
                          {c.count} value{c.count !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500 font-medium shrink-0">
                        {((c.count / total) * 100).toFixed(1)}%
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Question Effectiveness */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-1">Question Effectiveness</h3>
              <p className="text-sm text-gray-500 font-medium mb-5">Questions ranked by effectiveness score</p>
              {interviewQuestions.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircleQuestion size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm font-medium text-gray-500">No questions yet.</p>
                  <p className="text-xs text-gray-400 font-medium mt-1">Add questions to the library to see analytics.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...interviewQuestions]
                    .sort((a, b) => (b.targetValues?.length || 0) - (a.targetValues?.length || 0))
                    .slice(0, 5)
                    .map((q) => (
                      <div
                        key={q.id}
                        className="border border-gray-100 rounded-xl p-3.5 hover:border-gray-200 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 leading-snug">
                              {q.text}
                            </p>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                              Evaluates {(q.targetValues?.length || 0)} value{q.targetValues?.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                          <span className="text-sm font-black text-gray-900 shrink-0 mt-0.5">
                            —
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Target Value Dialog */}
      <Dialog open={showValueDialog} onOpenChange={setShowValueDialog}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {editingValue ? "Edit Target Value" : "Add Target Value"}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g., Excellence"
                className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                value={valueName}
                onChange={(e) => setValueName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Category</label>
              <Select value={valueCategory} onValueChange={setValueCategory}>
                <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100 w-full">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-xl border border-gray-200 shadow-lg">
                  <SelectGroup>
                    <SelectItem value="Leadership">Leadership</SelectItem>
                    <SelectItem value="Collaboration">Collaboration</SelectItem>
                    <SelectItem value="Excellence">Excellence</SelectItem>
                    <SelectItem value="Integrity">Integrity</SelectItem>
                    <SelectItem value="Innovation">Innovation</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Description</label>
              <Textarea
                placeholder="Describe this core value..."
                className="bg-gray-50 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-100 min-h-[80px] resize-y"
                value={valueDescription}
                onChange={(e) => setValueDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setShowValueDialog(false)}
                disabled={targetValueSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
                onClick={handleSaveValue}
                disabled={targetValueSubmitting}
              >
                {targetValueSubmitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : editingValue ? (
                  "Save Changes"
                ) : (
                  "Add Value"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Interview Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {editingQuestion ? "Edit Question" : "Add New Question"}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 font-medium">
              Create a new interview question for the library
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Question */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Question <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Enter the interview question..."
                className="bg-gray-50 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-100 min-h-[72px] resize-y"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
              />
            </div>

            {/* Category + Level */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Category</label>
                <Select value={questionCategory} onValueChange={setQuestionCategory}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100 w-full">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border border-gray-200 shadow-lg">
                    <SelectGroup>
                      <SelectItem value="Values Alignment">Values Alignment</SelectItem>
                      <SelectItem value="Personality Fit">Personality Fit</SelectItem>
                      <SelectItem value="Leadership Potential">Leadership Potential</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Level</label>
                <Select value={questionLevel} onValueChange={setQuestionLevel}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100 w-full">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border border-gray-200 shadow-lg">
                    <SelectGroup>
                      <SelectItem value="All Levels">All Levels</SelectItem>
                      <SelectItem value="Entry">Entry</SelectItem>
                      <SelectItem value="Mid">Mid</SelectItem>
                      <SelectItem value="Senior">Senior</SelectItem>
                      <SelectItem value="Executive">Executive</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Description (Optional)</label>
              <Textarea
                placeholder="Guidance for interviewers on what this question evaluates..."
                className="bg-gray-50 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-100 min-h-[72px] resize-y"
                value={questionDescription}
                onChange={(e) => setQuestionDescription(e.target.value)}
              />
            </div>

            {/* Values This Question Evaluates */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Values This Question Evaluates</label>
              <MultiSelect
                options={targetValues.map((v) => ({ value: v.name, label: v.name }))}
                selected={questionTargetValues}
                onChange={setQuestionTargetValues}
                placeholder="Select target values..."
              />
              {targetValues.length === 0 && (
                <p className="text-xs text-amber-600 font-medium">
                  No target values available. Add some in the Core Values tab first.
                </p>
              )}
            </div>

            {/* Follow-up Questions */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-900">Follow-up Questions</label>
              {questionFollowUps.length > 0 && (
                <div className="border border-gray-100 rounded-xl divide-y divide-gray-50 bg-gray-50/30">
                  {questionFollowUps.map((fq, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 group"
                    >
                      <span className="text-sm text-gray-700 font-medium whitespace-normal break-words">
                        {fq}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuestionFollowUps((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-gray-300 hover:text-red-500 transition-colors shrink-0 p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter a follow-up question..."
                  value={followUpInput}
                  onChange={(e) => setFollowUpInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && followUpInput.trim()) {
                      e.preventDefault();
                      setQuestionFollowUps((prev) => [...prev, followUpInput.trim()]);
                      setFollowUpInput("");
                    }
                  }}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-4 shrink-0"
                  onClick={() => {
                    if (followUpInput.trim()) {
                      setQuestionFollowUps((prev) => [...prev, followUpInput.trim()]);
                      setFollowUpInput("");
                    }
                  }}
                  disabled={!followUpInput.trim()}
                >
                  <Plus size={14} />
                  Add
                </Button>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-900">Tags</label>
              {questionTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {questionTags.map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => setQuestionTags((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-blue-400 hover:text-red-500 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && tagInput.trim()) {
                      e.preventDefault();
                      setQuestionTags((prev) => [...prev, tagInput.trim()]);
                      setTagInput("");
                    }
                  }}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-4 shrink-0"
                  onClick={() => {
                    if (tagInput.trim()) {
                      setQuestionTags((prev) => [...prev, tagInput.trim()]);
                      setTagInput("");
                    }
                  }}
                  disabled={!tagInput.trim()}
                >
                  <Plus size={14} />
                  Add
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setShowQuestionDialog(false)}
                disabled={questionSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
                onClick={handleSaveQuestion}
                disabled={questionSubmitting}
              >
                {questionSubmitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : editingQuestion ? (
                  "Save Changes"
                ) : (
                  "Create Question"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal — Target Value */}
      <AlertDialog open={deleteModalOpen && !!valueToDelete} onOpenChange={(open) => { if (!open) { setDeleteModalOpen(false); setValueToDelete(null); } }}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Target Value</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{valueToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={valueDeleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteValue}
              disabled={valueDeleteLoading}
            >
              {valueDeleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Value"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Modal — Interview Question */}
      <AlertDialog open={deleteModalOpen && !!questionToDelete} onOpenChange={(open) => { if (!open) { setDeleteModalOpen(false); setQuestionToDelete(null); } }}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Question</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{questionToDelete?.text}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={questionDeleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteQuestion}
              disabled={questionDeleteLoading}
            >
              {questionDeleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Question"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ValuesManagement;
